<template>
  <div>
    <div
      class="progress-bar__mobile"
      :class="route.name + banxaNativeClass"
      v-if="route.name !== 'summary'"
    >
      <div
        v-for="(section, index) in progressSections"
        :key="section.id"
        class="progress-bar__mobile__el"
        :class="progressState[section.id]"
      >
        <component :is="section.icon" />
        <p :class="banxaNativeClass">{{ section.title }}</p>
        <div
          v-if="index < progressSections.length - 1"
          class="progress-bar__mobile__divider"
        />
      </div>
    </div>

    <div
      class="progress-bar__desktop"
      :class="route.name + banxaNativeClass"
    >
      <div
        v-for="section in progressSections"
        :key="section.id"
        class="progress-bar__desktop__el"
        :class="progressState[section.id]"
      >
        <div class="progress-bar__desktop__background" />
        <div class="progress-bar__desktop__icon">
          <component :is="section.icon" />
        </div>
        <div class="progress-bar__desktop__copy">
          <h2 class="large-headline">{{ section.title }}</h2>
          <p>
            {{ section.text }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useGlobalStore } from '@/stores/globalStore';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import VerificationIcon from '@/components/icons/Verification.vue';
import PaymentIcon from '@/components/icons/Payment.vue';
import IdentityIcon from '@/components/icons/Identity.vue';
import { expressCheckoutEnabled } from '@/composables/router/handleExpressCheckout';

const { t } = useI18n();
const store = useGlobalStore();
const orderStore = useOrderFormStore();
const route = useRoute();

const banxaNativeClass = computed(() => (store.getIsBanxaNative ? ' banxa-native' : ''));

defineProps({
  progressState: {
    type: Object,
    required: true,
  },
});

/**
 * Computed property returning an array of progress bar sections.
 * Normal flow is Verification, Identity, Payment.
 * If express checkout is enabled, Payment section is moved to the front.
 * Express flow is Payment, Verification, Identity.
 * Each section contains an id, stateKey, icon, title, and text.
 * @returns {Array<Object>} Array of progress sections
 * @property {string} id - Unique identifier for the section
 * @property {string} stateKey - Key for the section's state
 * @property {string} icon - Icon for the section
 * @property {string} title - Title for the section
 * @property {string} text - Text for the section
 */

const progressSections = computed(() => {
  const progress = [
    {
      id: 'verification',
      stateKey: 'verification',
      icon: VerificationIcon,
      title: t('ProgressBar.label.verify'),
      text: t('ProgressBar.text.verify'),
    },
    {
      id: 'identity',
      stateKey: 'identity',
      icon: IdentityIcon,
      title: t('ProgressBar.label.identify'),
      text: t('ProgressBar.text.identify'),
    },
    {
      id: 'payment',
      stateKey: 'payment',
      icon: PaymentIcon,
      title: orderStore.isBuyMode ? t('ProgressBar.label.payment') : t('ProgressBar.label.deposit'),
      text: orderStore.isBuyMode ? t('ProgressBar.text.payment') : t('ProgressBar.text.deposit'),
    },
  ];

  if (expressCheckoutEnabled()) {
    const paymentSectionIndex = progress.findIndex((section) => section.id === 'payment');
    if (paymentSectionIndex > -1) {
      const [paymentSection] = progress.splice(paymentSectionIndex, 1);
      progress.unshift(paymentSection);
    }
  }

  return progress;
});
</script>

<style lang="scss">
/* Mobile version styles */
.progress-bar__mobile {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 2rem;
  transition: opacity 250ms;
  opacity: 1;
  padding-top: var(--padding-1);

  @media (min-width: 768px) {
    display: none;
  }

  &.order {
    opacity: 0.2;
  }

  &__el {
    position: relative;
    svg {
      width: 3.2rem;
    }

    p {
      position: absolute;
      left: 50%;
      transform: translateX(-50%) translateY(1rem);

      @include breakpoint($min-m) {
        font-size: $font-head;
      }

      @include breakpoint($min-m) {
        display: none;
      }
    }
  }

  &__divider {
    flex: 1;
    background: var(--input-background);
    height: 1px;
    position: relative;

    &:after {
      content: "";
      width: 100%;
      height: 100%;
      background: var(--secondary);
      position: absolute;
      transform-origin: 0 0;
      transform: scaleX(0);
      transition: transform 750ms ease-in-out;
    }

    &.INCOMPLETE {
      &:after {
        transform: scaleX(0);
      }
    }

    &.PENDING {
      &:after {
        transform: scaleX(0.5);
      }
    }

    &.COMPLETE {
      &:after {
        transform: scaleX(1);
      }
    }
  }
}

/* Desktop version styles */
.progress-bar__desktop {
  display: none;
  transition: opacity 250ms;

  @media (min-width: 768px) {
    display: grid;
  }

  &.order {
    opacity: 0.2;
  }

  grid-template-columns: repeat(1, 1fr);
  grid-auto-rows: 1fr;
  grid-row-gap: 1.8rem;
  padding: 2rem 0;

  &__el {
    padding: 1.8rem 0;
    position: relative;
    display: flex;

    &.PENDING {
      opacity: 1;
    }

    &.COMPLETE {
      opacity: 0.25;
      transition: opacity 1000ms;
      transition-delay: 1000ms;
    }

    &.INCOMPLETE,
    &.order {
      opacity: 0.25;
    }
  }

  &__icon {
    padding: 0 2rem;
    svg {
      width: 3.2rem;
    }
  }

  &__copy {
    h2 {
      margin-bottom: 0.8rem;
    }
    p {
      opacity: 0.7;
      font-size: 1.2rem;
      line-height: 1.5;
    }
  }

  &__background {
    width: calc(100% + 3.4rem);
    height: 100%;
    background-color: var(--input-background);
    position: absolute;
    top: 0;
    z-index: -1;
    border-radius: $border-radius 0 0 $border-radius;

    .INCOMPLETE & {
      opacity: 0;
    }

    .COMPLETE & {
      opacity: 0;
      transition: opacity 0ms;
      transition-delay: 1000ms;
    }
  }
}

.progress-bar__mobile.banxa-native,
.progress-bar__desktop.banxa-native {
  display: none;
}

p.banxa-native {
  display: block;
}
</style>
